import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import type { InsuranceType } from '../../../../../../services/EncounterService/types/encounterTypes';
import { SUFFIXES, RELATIONSHIP_CODES, US_STATES } from '../constants';

interface InsuredInfoSectionProps {
  formData: InsuranceType;
  sameAsPatient: boolean;
  isDisabled: boolean;
  onInputChange: (field: keyof InsuranceType, value: string) => void;
  onSameAsPatientChange: (checked: boolean) => void;
}

export const InsuredInfoSection: React.FC<InsuredInfoSectionProps> = ({
  formData,
  sameAsPatient,
  isDisabled,
  onInputChange,
  onSameAsPatientChange
}) => {
  const isFieldDisabled = isDisabled || sameAsPatient;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Insured's Information
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={sameAsPatient}
              onChange={(e) => onSameAsPatientChange(e.target.checked)}
              disabled={isDisabled}
            />
          }
          label="Same as Patient"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              color: isDisabled ? '#9e9e9e' : '#64748b'
            }
          }}
        />
      </Stack>

      {!sameAsPatient && (
        <Stack spacing={2}>
          {/* Name Fields */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              size="small"
              value={formData.insuredsFirstName || ''}
              onChange={(e) => onInputChange('insuredsFirstName', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
            <TextField
              label="Middle Name"
              size="small"
              value={formData.insuredsMiddleName || ''}
              onChange={(e) => onInputChange('insuredsMiddleName', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
            <TextField
              label="Last Name"
              size="small"
              value={formData.insuredsLastName || ''}
              onChange={(e) => onInputChange('insuredsLastName', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
            <FormControl sx={{ width: 120 }} size="small">
              <InputLabel>Suffix</InputLabel>
              <Select
                value={formData.insuredsSuffix || ''}
                onChange={(e) => onInputChange('insuredsSuffix', e.target.value)}
                disabled={isFieldDisabled}
                label="Suffix"
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {SUFFIXES.map((suffix) => (
                  <MenuItem key={suffix} value={suffix}>
                    {suffix}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Relationship and DOB */}
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>Relationship</InputLabel>
              <Select
                value={formData.insuredsRelationship || '09'}
                onChange={(e) => onInputChange('insuredsRelationship', e.target.value)}
                disabled={isFieldDisabled}
                label="Relationship"
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                  }
                }}
              >
                {RELATIONSHIP_CODES.map((rel) => (
                  <MenuItem key={rel.code} value={rel.code}>
                    {rel.code} - {rel.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Date of Birth"
              type="date"
              size="small"
              value={formData.insuredsDateOfBirth || ''}
              onChange={(e) => onInputChange('insuredsDateOfBirth', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              InputLabelProps={{ shrink: true }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
          </Stack>

          {/* Address Fields */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Street Address 1"
              size="small"
              value={formData.insuredsStreet1 || ''}
              onChange={(e) => onInputChange('insuredsStreet1', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 2,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
            <TextField
              label="Street Address 2"
              size="small"
              value={formData.insuredsStreet2 || ''}
              onChange={(e) => onInputChange('insuredsStreet2', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 2,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
          </Stack>

          {/* City, State, ZIP */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="City"
              size="small"
              value={formData.insuredsCity || ''}
              onChange={(e) => onInputChange('insuredsCity', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 2,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
            <FormControl sx={{ flex: 1 }} size="small">
              <InputLabel>State</InputLabel>
              <Select
                value={formData.insuredsState || ''}
                onChange={(e) => onInputChange('insuredsState', e.target.value)}
                disabled={isFieldDisabled}
                label="State"
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                  }
                }}
              >
                <MenuItem value="">Select State</MenuItem>
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="ZIP Code"
              size="small"
              value={formData.insuredsZip || ''}
              onChange={(e) => onInputChange('insuredsZip', e.target.value)}
              disabled={isFieldDisabled}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  backgroundColor: isFieldDisabled ? '#f5f5f5' : 'inherit'
                }
              }}
              helperText={isDisabled ? 'Read-only (Encounter Cancelled)' : sameAsPatient ? 'Auto-filled from patient data' : ''}
            />
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
